import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  MapPin, BookOpen, GraduationCap, ArrowRight } from 'lucide-react';

const UNCChapelHill: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "UNC Chapel Hill";

    return (
        <div className="min-h-screen bg-white font-serif text-[#13294B] selection:bg-[#4B9CD3] selection:text-white">
            {/* Top Bar - Carolina Blue */}
             <div className="bg-[#4B9CD3] text-white py-3 px-6 text-center font-sans font-bold text-xs uppercase tracking-widest">
                 Public education for the public good.
             </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-[#e1e1e1]">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="text-3xl font-bold tracking-tight text-[#13294B]">{schoolName.split(' ')[0]}</span>
                        <span className="text-xs uppercase tracking-widest font-sans font-bold text-[#4B9CD3]">{schoolName.split(' ').slice(1).join(' ')}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-sans font-bold text-[#13294B] uppercase text-xs tracking-widest">
                        <a href="#" className="hover:text-[#4B9CD3] transition-colors">About</a>
                        <a href="#" className="hover:text-[#4B9CD3] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#4B9CD3] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#4B9CD3] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#4B9CD3] transition-colors">Visitors</a>
                    </div>
                    
                    <div className="flex gap-4 text-[#13294B]">
                         <Search className="cursor-pointer hover:text-[#4B9CD3]" size={22} />
                         <Menu className="cursor-pointer lg:hidden hover:text-[#4B9CD3]" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - The Old Well */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1558522195-e1201b090344?q=80&w=2070&auto=format&fit=crop" 
                    alt="Old Well" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#13294B]/80 via-transparent to-transparent"></div>

                <div className="absolute bottom-0 w-full p-12 text-center text-white">
                     <h1 className="text-5xl md:text-7xl mb-6 font-bold drop-shadow-lg">
                         Lux Libertas.
                     </h1>
                     <p className="text-xl md:text-2xl mb-12 font-sans font-light max-w-2xl mx-auto drop-shadow-md">
                         Light and Liberty. The nation's first public university.
                     </p>
                     <Button className="bg-[#4B9CD3] hover:bg-[#3B8AC0] text-white font-sans font-bold uppercase tracking-widest px-10 h-14 rounded-full shadow-lg">
                         Discover {schoolName.split(' ')[0]}
                     </Button>
                </div>
            </header>

            {/* Grid of Interests */}
            <section className="bg-white py-24 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl text-[#13294B] mb-16">
                        What's Happening at {schoolName.split(' ')[0]}?
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                        {[
                            { title: "Groundbreaking Research", t: "Science", img: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?q=80&w=2070&auto=format&fit=crop"},
                            { title: `${schoolName.split(' ')[0]} Athletics`, t: "Sports", img: "https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?q=80&w=2069&auto=format&fit=crop"},
                             { title: "Arts Everywhere", t: "Culture", img: "https://images.unsplash.com/photo-1514533450685-449339466095?q=80&w=2070&auto=format&fit=crop"}
                        ].map((item, i) => (
                            <div key={i} className="group cursor-pointer">
                                <div className="h-64 mb-4 overflow-hidden relative rounded-lg">
                                    <img src={item.img} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" alt={item.title}/>
                                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 text-[#13294B] font-sans text-[10px] font-bold uppercase tracking-widest rounded-sm">
                                        {item.t}
                                    </div>
                                </div>
                                <h3 className="text-2xl font-bold text-[#13294B] leading-tight group-hover:text-[#4B9CD3] transition-colors">{item.title}</h3>
                                <div className="mt-4 flex items-center gap-2 text-[#4B9CD3] text-sm font-sans font-bold uppercase tracking-widest group-hover:gap-3 transition-all">
                                    Read More <ArrowRight size={14}/>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer - Argyle Pattern Hint */}
            <footer className="bg-[#13294B] text-white pt-20 pb-10 px-6 md:px-12 border-t-[10px] border-[#4B9CD3]">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 font-sans text-sm">
                    <div className="col-span-1 md:col-span-2">
                         <span className="text-3xl font-serif font-bold mb-4 block text-white">{schoolName}</span>
                         <p className="text-[#aebecd] mb-6 max-w-xs">
                             {schoolName} <br/>
                             Chapel Hill, NC 27599 <br/>
                             (919) 962-2211
                         </p>
                         <div className="flex gap-4">
                             <div className="w-8 h-8 bg-[#4B9CD3] rounded-full hover:bg-white transition-colors cursor-pointer"></div>
                             <div className="w-8 h-8 bg-[#4B9CD3] rounded-full hover:bg-white transition-colors cursor-pointer"></div>
                             <div className="w-8 h-8 bg-[#4B9CD3] rounded-full hover:bg-white transition-colors cursor-pointer"></div>
                         </div>
                    </div>

                    <div>
                        <h4 className="text-[#4B9CD3] font-bold uppercase tracking-widest mb-6 text-xs">Resources</h4>
                        <ul className="space-y-3 text-white">
                            <li><a href="#" className="hover:text-[#4B9CD3]">{schoolName.split(' ')[0]} Directory</a></li>
                            <li><a href="#" className="hover:text-[#4B9CD3]">Libraries</a></li>
                            <li><a href="#" className="hover:text-[#4B9CD3]">Campus Map</a></li>
                            <li><a href="#" className="hover:text-[#4B9CD3]">Careers</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-[#4B9CD3] font-bold uppercase tracking-widest mb-6 text-xs">Policies</h4>
                        <ul className="space-y-3 text-white">
                            <li><a href="#" className="hover:text-[#4B9CD3]">Accessibility</a></li>
                            <li><a href="#" className="hover:text-[#4B9CD3]">Privacy </a></li>
                            <li><a href="#" className="hover:text-[#4B9CD3]">Copyright</a></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-[1200px] mx-auto mt-16 text-center text-[#aebecd] text-xs">
                    © 2026 {schoolName}
                </div>
            </footer>
        </div>
    );
};

export default UNCChapelHill;
